import {ReactNode} from 'react';
import {CompanyFeedback} from 'views/home/CompanyFeedback';

import styles from './Dashboard.module.css';
import {DashboardEditGrid} from './DashboardEditGrid';
import {resolveTileSpan, useEffectiveColumns} from './dashboardLayout';
import {DashboardState, TileId, TileSpan} from './dashboardModel';
import {DashboardTileProvider} from './DashboardTileContext';
import {MapBannerRow} from './MapBanner';
import {appSlugFromTileId} from './pinnedApps';
import {SortableTile} from './SortableTile';
import {TileEditSurface} from './TileEditSurface';
import {AgendaTile} from './tiles/AgendaTile';
import {AppEmbedTile} from './tiles/AppEmbedTile';
import {CalendarTile} from './tiles/CalendarTile';
import {EarningsTile} from './tiles/earnings/EarningsTile';
import {GratitudeTile} from './tiles/GratitudeTile';
import {JiraTile} from './tiles/JiraTile';
import {MeetSomeoneTile} from './tiles/MeetSomeoneTile';
import {NewArrivalsTile} from './tiles/NewArrivalsTile';
import {NewsTile} from './tiles/NewsTile';
import {StatTile} from './tiles/StatTile';
import {TeamTimezonesTile} from './tiles/TeamTimezonesTile';
import {WorkstreamsTile} from './tiles/WorkstreamsTile';
import {TileSpanProvider} from './TileSizeContext';

interface TileRenderProps {
  state: DashboardState;
  tileSizes?: Partial<Record<TileId, TileSpan>>;
  editing?: boolean;
}

const renderTileContent = (tileId: TileId, props: TileRenderProps): ReactNode => {
  const {state} = props;
  const statById = (id: string) => state.stats.find((s) => s.id === id);
  const appSlug = appSlugFromTileId(tileId);
  if (appSlug) {
    return <AppEmbedTile slug={appSlug} />;
  }

  switch (tileId) {
    case 'stock': {
      const data = statById('stock');
      return data ? <StatTile data={data} isLoading={state.stockLoading} /> : null;
    }
    case 'earnings':
      return <EarningsTile />;
    case 'agenda':
      return <AgendaTile state={state} />;
    case 'teamTimezones':
      return <TeamTimezonesTile state={state} />;
    case 'news':
      return <NewsTile state={state} />;
    case 'newArrivals':
      return <NewArrivalsTile state={state} />;
    case 'calendar':
      return <CalendarTile />;
    case 'workstreams':
      return <WorkstreamsTile state={state} />;
    case 'jira':
      return <JiraTile state={state} />;
    case 'gratitude':
      return <GratitudeTile state={state} />;
    case 'meetSomeone':
      return <MeetSomeoneTile state={state} />;
    case 'companyFeedback':
      return <CompanyFeedback />;
    default:
      return null;
  }
};

const wrapTile = (tileId: TileId, props: TileRenderProps): ReactNode => {
  const span = resolveTileSpan(tileId, props.tileSizes ?? {});
  const content = renderTileContent(tileId, props);
  if (!content) {
    return null;
  }
  return (
    <DashboardTileProvider key={tileId} tileId={tileId}>
      <TileSpanProvider span={span}>{content}</TileSpanProvider>
    </DashboardTileProvider>
  );
};

const COLUMN_CLASS: Record<number, string> = {
  2: styles.cols2,
  3: styles.cols3,
  4: styles.cols4,
};

export const DashboardGrid = ({
  tileOrder,
  columns = 4,
  editing = false,
  tileSizes,
  onSetOrder,
  onRemoveTile,
  onSetTileSize,
  ...props
}: TileRenderProps & {
  tileOrder: TileId[];
  columns?: 2 | 3 | 4;
  onSetOrder?: (order: TileId[]) => void;
  onRemoveTile?: (tileId: TileId) => void;
  onSetTileSize?: (tileId: TileId, span: TileSpan) => void;
}) => {
  const gridClass = [styles.grid, editing ? styles.gridEditing : '', COLUMN_CLASS[columns]]
    .filter(Boolean)
    .join(' ');
  const renderProps: TileRenderProps = {...props, tileSizes, editing};
  const effectiveColumns = useEffectiveColumns(columns);

  if (!editing || !onRemoveTile || !onSetTileSize || !onSetOrder) {
    // The map banner row should always sit above the grid, as wide as the Workstreams tile.
    // When a column is left over, the feedback tile should move up
    // beside the map banner so that the two share a height.
    const workstreamsCols = tileOrder.includes('workstreams')
      ? resolveTileSpan('workstreams', tileSizes ?? {}).cols
      : 2;
    const mapBannerCols = Math.min(workstreamsCols, effectiveColumns);
    const mapCompanionId: TileId | undefined =
      mapBannerCols < effectiveColumns && tileOrder.includes('companyFeedback')
        ? 'companyFeedback'
        : undefined;
    const mapCompanion = mapCompanionId ? wrapTile(mapCompanionId, renderProps) : undefined;

    const tileNodes = tileOrder
      .filter((tileId) => !mapCompanion || tileId !== mapCompanionId)
      .map((tileId) => wrapTile(tileId, renderProps))
      .filter((node): node is ReactNode => node != null);
    return (
      <>
        <MapBannerRow
          columns={effectiveColumns}
          bannerCols={mapBannerCols}
          companion={mapCompanion}
        />
        <div className={gridClass}>{tileNodes}</div>
      </>
    );
  }

  return (
    <div className={gridClass}>
      <DashboardEditGrid
        tileIds={tileOrder}
        onSetOrder={onSetOrder}
        getTileSpan={(tileId) => resolveTileSpan(tileId, tileSizes ?? {})}
        renderOverlay={(tileId) => {
          const span = resolveTileSpan(tileId, tileSizes ?? {});
          const node = wrapTile(tileId, renderProps);
          return (
            <TileEditSurface tileId={tileId} span={span}>
              {node}
            </TileEditSurface>
          );
        }}
        renderTile={(tileId) => {
          const node = wrapTile(tileId, renderProps);
          if (!node) {
            return null;
          }
          const span = resolveTileSpan(tileId, tileSizes ?? {});
          return (
            <SortableTile
              key={tileId}
              tileId={tileId}
              span={span}
              onRemove={onRemoveTile}
              onSetSize={onSetTileSize}
            >
              {node}
            </SortableTile>
          );
        }}
      />
    </div>
  );
};
